<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

import { reportMethods, reportComputed } from "@/state/helpers";

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default {
    page: {
        title: "Stock Take",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Stock Take",
            items: [{ text: "Reports" }, { text: "Stock Take", active: true }],
            countedAt: todayIso(),
            search: "",
            activeOnly: true,
            counts: {}, // product_id -> { counted_qty, notes }
            submitting: false,
            exporting: false,
            confirmOpen: false,
            resultOpen: false,
            result: null,
        };
    },
    computed: {
        ...reportComputed,
        params() {
            return {
                ...(this.activeOnly ? { active: 1 } : {}),
                ...(this.search ? { search: this.search } : {}),
            };
        },
        rows() {
            return this.stock_rows.map((r) => {
                const entry = this.counts[r.id];
                const countedRaw = entry && entry.counted_qty !== "" && entry.counted_qty != null ? Number(entry.counted_qty) : null;
                const variance = countedRaw == null ? null : countedRaw - r.stock_quantity;
                return { ...r, _counted: entry ? entry.counted_qty : "", _notes: entry ? entry.notes : "", _variance: variance };
            });
        },
        pendingLines() {
            return Object.entries(this.counts)
                .filter(([, v]) => v && v.counted_qty !== "" && v.counted_qty != null)
                .map(([pid, v]) => ({
                    product_id: Number(pid),
                    counted_qty: Number(v.counted_qty),
                    notes: v.notes || null,
                }));
        },
        nonZeroVariances() {
            return this.rows.filter((r) => r._variance !== null && r._variance !== 0);
        },
    },
    mounted() {
        this.reload();
    },
    methods: {
        ...reportMethods,
        reload() {
            this.getStockList(this.params).catch(() => {});
        },
        ensureEntry(productId) {
            if (!this.counts[productId]) {
                this.$set(this.counts, productId, { counted_qty: "", notes: "" });
            }
        },
        onCountInput(productId, value) {
            this.ensureEntry(productId);
            this.counts[productId].counted_qty = value;
        },
        onNotesInput(productId, value) {
            this.ensureEntry(productId);
            this.counts[productId].notes = value;
        },
        varianceClass(v) {
            if (v == null) return "";
            if (v === 0) return "text-muted";
            return v > 0 ? "text-success font-weight-bold" : "text-danger font-weight-bold";
        },
        async doExport() {
            this.exporting = true;
            try {
                await this.exportStockXlsx(this.params);
            } finally {
                this.exporting = false;
            }
        },
        openConfirm() {
            if (!this.pendingLines.length) {
                this.$bvToast.toast("Enter at least one counted quantity first.", { variant: "warning", title: "Nothing to submit" });
                return;
            }
            this.confirmOpen = true;
        },
        async submit() {
            this.submitting = true;
            try {
                const data = await this.submitStockTake({
                    counted_at: this.countedAt,
                    lines: this.pendingLines,
                });
                this.result = data;
                this.confirmOpen = false;
                this.resultOpen = true;
                this.counts = {};
                this.reload();
            } catch (e) {
                this.$bvToast.toast("Stock take failed. Please try again.", { variant: "danger", title: "Error" });
            } finally {
                this.submitting = false;
            }
        },
    },
    watch: {
        activeOnly: "reload",
    },
};
</script>
<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="row mb-3 align-items-end">
            <div class="col-md-3">
                <label class="form-label small text-muted mb-1">Counted on</label>
                <b-form-input type="date" v-model="countedAt" />
            </div>
            <div class="col-md-4">
                <label class="form-label small text-muted mb-1">Filter</label>
                <b-input-group>
                    <b-form-input v-model="search" placeholder="SKU or product name" @keyup.enter="reload" />
                    <b-input-group-append>
                        <b-button variant="primary" @click="reload"><i class="fa fa-search" /></b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>
            <div class="col-md-2">
                <b-form-checkbox v-model="activeOnly">Active only</b-form-checkbox>
            </div>
            <div class="col-md-3 text-md-right">
                <b-button variant="outline-secondary" @click="doExport" :disabled="exporting">
                    <i class="fa fa-print" /> {{ exporting ? "Building..." : "Print Sheet" }}
                </b-button>
            </div>
        </div>

        <b-card>
            <b-table-simple small striped hover responsive>
                <b-thead>
                    <b-tr>
                        <b-th>SKU</b-th>
                        <b-th>Product</b-th>
                        <b-th>Group</b-th>
                        <b-th class="text-right">System</b-th>
                        <b-th style="width:120px">Counted</b-th>
                        <b-th class="text-right">Variance</b-th>
                        <b-th style="width:200px">Notes</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-if="stock_loading">
                        <b-td colspan="7" class="text-center text-muted py-3">
                            <b-spinner small /> Loading…
                        </b-td>
                    </b-tr>
                    <b-tr v-else-if="!rows.length">
                        <b-td colspan="7" class="text-center text-muted py-3">No products.</b-td>
                    </b-tr>
                    <b-tr v-for="row in rows" :key="row.id">
                        <b-td>{{ row.sku }}</b-td>
                        <b-td>{{ row.name }}</b-td>
                        <b-td>{{ row.group }}</b-td>
                        <b-td class="text-right">{{ row.stock_quantity }}</b-td>
                        <b-td>
                            <b-form-input
                                type="number"
                                min="0"
                                size="sm"
                                :value="row._counted"
                                @input="onCountInput(row.id, $event)"
                            />
                        </b-td>
                        <b-td class="text-right" :class="varianceClass(row._variance)">
                            <span v-if="row._variance == null">—</span>
                            <span v-else>{{ row._variance > 0 ? "+" + row._variance : row._variance }}</span>
                        </b-td>
                        <b-td>
                            <b-form-input
                                size="sm"
                                placeholder="Optional"
                                :value="row._notes"
                                @input="onNotesInput(row.id, $event)"
                            />
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-card>

        <div class="mt-3 d-flex justify-content-between align-items-center">
            <div class="text-muted small">
                {{ pendingLines.length }} line(s) with counts entered, {{ nonZeroVariances.length }} with variance.
            </div>
            <b-button variant="primary" size="lg" @click="openConfirm" :disabled="submitting">
                <i class="fa fa-check" /> Submit Stock Take
            </b-button>
        </div>

        <b-modal v-model="confirmOpen" title="Confirm Stock Take" ok-title="Submit" :ok-disabled="submitting" @ok.prevent="submit">
            <p>You are about to submit a stock take for <strong>{{ countedAt }}</strong>.</p>
            <ul>
                <li>{{ pendingLines.length }} product(s) with counted quantities</li>
                <li>{{ nonZeroVariances.length }} will be adjusted (non-zero variance)</li>
                <li>{{ pendingLines.length - nonZeroVariances.length }} will be skipped (count matches system)</li>
            </ul>
            <p class="small text-muted">Each adjustment writes an audit log entry with action <code>stock_take</code>.</p>
        </b-modal>

        <b-modal v-model="resultOpen" title="Stock Take Complete" ok-only>
            <div v-if="result">
                <p>Counted on <strong>{{ result.counted_at }}</strong>.</p>
                <p>
                    <b-badge variant="success">{{ result.adjusted }} adjusted</b-badge>&nbsp;
                    <b-badge variant="secondary">{{ result.skipped }} skipped</b-badge>
                </p>
                <b-table v-if="result.lines && result.lines.length" :items="result.lines" small striped :fields="[
                    { key: 'product_id', label: 'Product ID' },
                    { key: 'before', label: 'Before' },
                    { key: 'after', label: 'After' },
                    { key: 'variance', label: 'Variance' },
                ]" />
            </div>
        </b-modal>
    </Layout>
</template>
